/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ItemInfo, TagCloudItemData } from './tagCloud.type';
import { getContrastingTextColor } from '#utils/PRIVATE_colorUtils/colorUtils';
import { getUnion, intersects, type Rect } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { BundleType } from '#resources/nls/bundle';
import { TagItemProps } from './TagCloudItem';
import { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { getItemPadding } from './tagCloudNavUtils';
import { cancelEvent } from '#utils/UNSAFE_visUtils';
import { generateAriaLabel } from '#utils/UNSAFE_visUtils/accUtils';
import type { JSX } from 'preact';

const BOTTOM_PADDING = 20;
const DEFAULT_FONT_SIZE = 12;

/**
 * helper layout functions for font size calc
 * @param {number} minValue
 * @param {number} maxValue
 * @param {number} range
 * @return {number}
 */
export const fontSizeCalc = (value: number, minValue: number, maxValue: number, range: number) => {
  return minValue === maxValue
    ? DEFAULT_FONT_SIZE
    : DEFAULT_FONT_SIZE +
        Math.ceil((((range - 1) * (value - minValue)) / (maxValue - minValue)) * DEFAULT_FONT_SIZE);
};

/**
 * returns initial font size arr of items for layout
 * @param {Array} items
 * @return {Array}
 */
export function getInitialFontSize<K>(items: TagCloudItemData<K>[]): TagItemProps<K>[] {
  const fontSizeArr = [];
  let minValue = Number.MAX_VALUE;
  let maxValue = -Number.MAX_VALUE;
  // First iterate thru data items to find the min and max values
  for (let i = 0; i < items.length; i++) {
    minValue = Math.min(minValue, items[i].value);
    maxValue = Math.max(maxValue, items[i].value);
  }

  for (let i = 0; i < items.length; i++) {
    const x = fontSizeCalc(items[i].value, minValue, maxValue, 3);
    fontSizeArr.push({ ...items[i], fontSize: x });
  }
  return fontSizeArr;
}

/**
 * Calculates the line breaks for rectangular layout
 * @param {Array} arDims an array of Dimension objects for each tag
 * @param {number} width the available width
 * @return {Array} an array containing the starting tag index for each line in the layout
 * @private
 */
export function calculateLineBreaks(arDims: Rect[], width: number) {
  const hPadding = 2;
  const lines = [0];
  let curWidth = arDims[0].w + hPadding;
  if (arDims.length > 1) {
    for (let i = 1; i < arDims.length; i++) {
      if (curWidth + arDims[i].w > width) {
        lines.push(i);
        curWidth = 0;
      }
      curWidth += arDims[i].w + hPadding;
    }
  }
  return lines;
}

/**
 * Returns the items position and font size in cloud layout.
 * @param items
 * @param tagcloudH
 * @param tagcloudW
 */
export function getCloudLayout<K>(
  items: TagCloudItemData<K>[],
  tagcloudW: number,
  tagcloudH: number,
  isRTL: boolean,
  getTextDimensions: GetTextDimensionsType
): TagItemProps<K>[] {
  const positions = [];
  const steps = 180;
  let xrStep = 10 / steps;
  let yrStep = 10 / steps;
  if (tagcloudW > tagcloudH) xrStep *= tagcloudW / tagcloudH;
  else yrStep *= tagcloudH / tagcloudW;
  const thetaStep = (2 * Math.PI) / steps;
  let bounds = null;
  let maxFontSize = 0;
  const cosCache = [];
  const sinCache = [];
  const tagWordData = [];
  const tagCloudX = 0; // availSpace.x
  const tagCloudY = 0; // availSpace.y

  const initialFontItems = getInitialFontSize(items);

  for (let i = 0; i < initialFontItems.length; i++) {
    let placed = false;
    let step = 0;
    let stepIncrement = 4;
    const tag = initialFontItems[i];
    const tagDimensions = getTextDimensions(tag.label, {
      fontSize: tag.fontSize + 'px'
    });
    maxFontSize = Math.max(maxFontSize, tag.fontSize);
    let lastCollisionIndex = -1;
    while (!placed) {
      // cache sin/cos values because Chrome is slow at these calculations
      const lookup = step % steps;
      if (cosCache[lookup] === undefined) cosCache[lookup] = Math.cos(step * thetaStep);
      if (sinCache[lookup] === undefined) sinCache[lookup] = Math.sin(step * thetaStep);
      const _x = xrStep * step * cosCache[lookup];
      const _y = yrStep * step * sinCache[lookup];
      const padding = getItemPadding(tagDimensions.height);
      const tagCloudDiv = {
        x: _x,
        y: _y,
        w: tagDimensions.width + 2 * padding,
        h: tagDimensions.height + 2 * padding
      }; // this needs to change
      placed = true;
      if (lastCollisionIndex !== -1) {
        if (intersects(positions[lastCollisionIndex], tagCloudDiv)) {
          placed = false;
        }
      }

      if (placed) {
        for (let j = 0; j < i; j++) {
          if (intersects(positions[j], tagCloudDiv)) {
            lastCollisionIndex = j;
            placed = false;
            break;
          }
        }
      }

      if (placed) {
        if (!bounds) {
          bounds = tagCloudDiv;
        } else {
          bounds = getUnion(bounds, tagCloudDiv);
        }
        lastCollisionIndex = -1;
        positions[i] = tagCloudDiv;
        tag.x = _x; // tag.setX(x)
        tag.y = _y; // tag.setY(-tagCloudDiv.y + y);
        tag.w = tagDimensions.width;
        tag.h = tagDimensions.height;
      }
      // Adjust stepIncrement so we check smaller degrees as spiral loops out
      if (step == 3600) {
        // 20 loops
        stepIncrement = 3;
      } else if (step == 5400) {
        // 30 loops
        stepIncrement = 2;
      } else if (step == 10800) {
        // 60 loops
        stepIncrement = 1;
      }
      step += stepIncrement;
    }
  }

  if (bounds) {
    const scale = Math.max(bounds.w / tagcloudW, bounds.h / tagcloudH);
    const cx = bounds.x + bounds.w / 2;
    const cy = bounds.y + bounds.h / 2;

    for (let k = 0; k < initialFontItems.length; k++) {
      let tagItemX;
      const tagItem = initialFontItems[k];
      if (!isRTL) {
        tagItemX =
          tagItem.x != null ? tagCloudX + tagItem.x / scale + (tagcloudW / 2 - cx / scale) : 0;
      } else {
        tagItemX =
          tagItem.x != null ? tagCloudX - tagItem.x / scale - (tagcloudW / 2 - cx / scale) : 0;
      }

      const tagItemY =
        tagItem.y != null ? tagCloudY + tagItem.y / scale + (tagcloudH / 2 - cy / scale) : 0;
      const tagItemW = tagItem.w != null ? tagItem.w / scale : 0;
      const tagItemH = tagItem.h != null ? tagItem.h / scale : 0;
      const fontSize = tagItem.fontSize;
      const tagItemFontSize = fontSize / scale;
      tagWordData.push({
        ...tagItem,
        x: tagItemX,
        y: tagItemY,
        fontSize: tagItemFontSize,
        w: tagItemW,
        h: tagItemH
      });
    }
  }
  return tagWordData;
}
/**
 * Returns the items position and font size in rectangle layout.
 * @param items
 * @param tagcloudH
 * @param tagcloudW
 * @param isRTL
 * @param getTextDimensions
 */
export function getRectangleLayout<K>(
  items: TagCloudItemData<K>[],
  tagcloudW: number,
  tagcloudH: number,
  isRTL: boolean,
  getTextDimensions: GetTextDimensionsType
) {
  const tagWordData = [];
  const arDims = [];
  let maxWidth = 0;
  let maxHeight = 0;
  let maxFontSize = 0;
  const vPadding = 2;
  const margin = 0;
  const tagCloudX = 0; // availSpace.x
  const tagCloudY = 0; // availSpace.y

  // initial items processing
  const initialFontItems = getInitialFontSize(items);

  // Get the unscaled dimensions
  for (let i = 0; i < initialFontItems.length; i++) {
    const tag = initialFontItems[i];
    const tagDimensions = getTextDimensions(tag.label, {
      fontSize: tag.fontSize + 'px'
    });
    maxWidth = Math.max(maxWidth, tagDimensions.width);
    maxHeight = Math.max(maxHeight, tagDimensions.height);
    maxFontSize = Math.max(maxFontSize, tag.fontSize);
    arDims.push({
      w: tagDimensions.width,
      h: tagDimensions.height,
      x: tagDimensions.x,
      y: tagDimensions.y
    });
  }

  // Iteratively calculate the ideal font scale
  let minScale = 0;
  let maxScale = (tagcloudW - 2 * margin) / maxWidth; // This is the max scale that can be applied before the longest word will no longer fit
  let scale;
  let arLines;
  while (maxScale - minScale > 0.001) {
    scale = (minScale + maxScale) / 2;
    arLines = calculateLineBreaks(arDims, (tagcloudW - 2 * margin) / scale);
    const h = arLines.length * (scale * maxHeight + vPadding) - vPadding;
    if (h > tagcloudH - 2 * margin) {
      maxScale = scale;
    } else {
      minScale = scale;
    }
  }

  // Calculate final line breaks
  scale = minScale;
  arLines = calculateLineBreaks(arDims, (tagcloudW - 2 * margin) / scale);
  arLines.push(initialFontItems.length);
  for (let k = 0; k < arLines.length - 1; k++) {
    const lineStart = arLines[k];
    const lineEnd = arLines[k + 1];
    let hPadding = 5;
    let maxLineHeight = 0;
    let justified = true;
    if (lineEnd - lineStart > 1) {
      let lineWidth = 0;
      for (let j = lineStart; j < lineEnd; j++) {
        lineWidth += arDims[j].w * scale;
        maxLineHeight = Math.max(maxLineHeight, arDims[j].h * scale);
      }
      hPadding = (tagcloudW - 2 * margin - lineWidth) / (lineEnd - lineStart - 1);

      if (k == arLines.length - 2) {
        // Don't justify the last line unless flowing it with a gap of .5 * maxLineHeight fills more than
        // 90% of the width
        const flowPadding = 0.5 * maxLineHeight;
        if (
          flowPadding < hPadding &&
          lineWidth + (lineEnd - lineStart) * flowPadding < 0.9 * (tagcloudW - 2 * margin)
        ) {
          hPadding = flowPadding;
          justified = false;
        }
      }
    }

    const bottomY = margin + (k + 1) * (maxHeight * scale + vPadding) - vPadding;
    let curX = margin;
    for (let m = lineStart; m < lineEnd; m++) {
      const tagLine = initialFontItems[m];
      const fontSize = tagLine.fontSize;
      const tagItemFontSize = fontSize * scale;
      tagLine.fontSize = tagItemFontSize;
      const tagDimensionsWidth = arDims[m].w * scale;
      tagLine.y = tagCloudY + bottomY + arDims[m].y * scale - BOTTOM_PADDING;
      if (justified && m === lineEnd - 1 && m !== lineStart) {
        if (!isRTL) tagLine.x = tagCloudX + tagcloudW - tagDimensionsWidth - margin;
        else tagLine.x = tagCloudX + tagDimensionsWidth + margin - tagcloudW;
        // alignRight(tagLine);
      } else {
        // alignLeft(tagLine);
        tagLine.x = tagCloudX + curX;
        if (!isRTL) {
          curX += arDims[m].w * scale + hPadding;
        } else {
          // tagLine.alignRight();
          curX -= arDims[m].w * scale + hPadding;
        }
      }
      tagWordData.push({ ...tagLine, w: tagDimensionsWidth, h: arDims[m].h * scale });
    }
  }
  return tagWordData;
}

/**
 * Calls the right layout algorithm based on tagcloud type.
 * @param layout cloud or rectangle. cloud if not defined.
 * @param items
 * @param tagcloudW tagcloud width
 * @param tagcloudH tagcloud height
 * @param isRTL
 * @param getTextDimensions
 */
export function getLayout<K>(
  layout: string | undefined,
  items: TagCloudItemData<K>[],
  tagcloudW: number,
  tagcloudH: number,
  isRTL: boolean,
  getTextDimensions: GetTextDimensionsType
) {
  let layoutTagCloud;
  if (layout === 'cloud' || !layout) {
    layoutTagCloud = getCloudLayout(items, tagcloudW, tagcloudH, isRTL, getTextDimensions);
  } else {
    layoutTagCloud = getRectangleLayout(items, tagcloudW, tagcloudH, isRTL, getTextDimensions);
  }
  return layoutTagCloud;
}

/**
 * Checks if the item is highlighted.
 * @param id Id of the item
 * @param highlighted The set of highlighted item
 */
export function isItemHighlighted<K>(id: K, highlightedIds?: K[]) {
  if (!highlightedIds) {
    return;
  }
  if (highlightedIds.length === 0) {
    return true;
  }
  return new Set(highlightedIds).has(id);
}

/**
 * Checks if the item is highlighted.
 * @param id Id of the item
 * @param selectedIds The set of highlighted item
 */
export function isItemSelected<K>(id: K, selectedIds?: K[]) {
  if (!selectedIds || selectedIds.length === 0) {
    return false;
  }
  return new Set(selectedIds).has(id);
}

/**
 * Returns the aria properties for the tag cloud.
 * @param isReadOnly { boolean } If the tag cloud is readonly.
 * @param accessibleLabel { string } The aria-label set by the app.
 * @returns The aria props.
 */
export function getAriaProps(
  translations: BundleType,
  hasData?: boolean,
  accessibleLabel?: string,
  ariaDescribedBy?: string,
  ariaLabelledBy?: string
): {
  'aria-label': UnpackSignals<JSX.AriaAttributes>['aria-label'];
  role: UnpackSignals<JSX.HTMLAttributes>['role'];
  'aria-describedby': UnpackSignals<JSX.AriaAttributes>['aria-describedby'];
  'aria-labelledby': UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
} {
  return {
    'aria-label': `${accessibleLabel || ''} ${hasData ? '' : translations.vis_noData()}`,
    role: 'application',
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': ariaLabelledBy
  };
}

/**
 * Returns the aria properties for the tag cloud.
 * @param isReadOnly { boolean } If the tag cloud is readonly.
 * @param accessibleLabel { string } The aria-label set by the app.
 * @returns The aria props.
 */
export function getItemAriaProps(
  supportsSelection: boolean,
  translations?: BundleType,
  isSelected?: boolean,
  accessibleLabel?: string,
  role?: UnpackSignals<JSX.HTMLAttributes['role']>
): {
  'aria-label': UnpackSignals<JSX.AriaAttributes>['aria-label'];
  role: UnpackSignals<JSX.HTMLAttributes['role']>;
} {
  return {
    'aria-label':
      generateAriaLabel(translations!, accessibleLabel!, {
        isSelected: !supportsSelection ? undefined : isSelected
      }) || undefined,
    role: role || 'img'
  };
}

/**
 * Returns the item props for BaseTagCloudItem.
 * @param item The tagcloud item.
 * @param itemIdx The item index of the item.
 * @param supportsSelection Whether the tagcloud supports selection
 * @param isReadOnly Whether the tagcloud is readonly.
 * @param hoveredItem The info of the currently hovered item.
 * @param selectedIds The selected ids.
 * @param focusedItem The info of currently focused item.
 * @param highlightedIds The highlighted ids.
 * @param activeId The active Id
 */
export function getItemProps<K>(
  item: TagCloudItemData<K>,
  itemIdx: number,
  supportsSelection: boolean,
  isReadOnly: boolean,
  hoveredItem?: ItemInfo,
  selectedIds?: Array<K>,
  focusedItem?: ItemInfo,
  highlightedIds?: Array<K>,
  activeId?: string
) {
  let isCurrent = false;
  let style;
  if (!isReadOnly) {
    const isFocusedItem = itemIdx === focusedItem?.idx;
    const isHoveredItem = itemIdx === hoveredItem?.idx;
    isCurrent = !!(
      (isFocusedItem && focusedItem?.isCurrent) ||
      (isHoveredItem && hoveredItem?.isCurrent)
    );
  }
  const isHighlighted = isItemHighlighted<K>(item.id, highlightedIds);
  const isSelected = isItemSelected(item.id, selectedIds);

  if (item.color) {
    let color, backgroundColor, backgroundImage;

    if (isSelected) {
      backgroundColor = item.color;
      color = getContrastingTextColor(backgroundColor);
    }
    // hovered and selection supported
    else color = item.color;

    style = {
      color,
      backgroundColor,
      backgroundImage
    };
  } else if (!supportsSelection) {
    style = {
      backgroundColor: 'none'
    };
  }

  return {
    isCurrent,
    isHighlighted: isHighlighted,
    isSelected: isSelected,
    isFocused: focusedItem ? hasFocusRing(focusedItem, itemIdx) : false,
    key: item.id,
    itemIdx,
    style,
    activeId: isCurrent ? activeId : undefined,
    isReadOnly,
    ...item
  };
}

/**
 * Returns the value of the data-oj-private-item-index attribute for a given element.
 * @param element The HTML element.
 */
export function getItemInfo(element: HTMLElement) {
  const itemIdx = element.dataset['idx'];
  if (itemIdx == null) return;

  return {
    idx: Number(itemIdx)
  };
}

/**
 * Compares if two ItemInfo are equal
 * @param item1
 * @param item2
 * @returns
 */
export function isEqualItem(item1?: ItemInfo, item2?: ItemInfo) {
  return item1?.idx === item2?.idx;
}

/**
 * Checks if the item is the currentItem and if the focus ring
 * is visible.
 * @param itemInfo ItemInfo
 * @param itemId Item index
 */
export function hasFocusRing(itemInfo: ItemInfo, itemIdx: number) {
  if (!itemInfo.isFocusVisible) return false;
  const { idx: idx } = itemInfo;

  return idx === itemIdx;
}

/**
 * Returns KeyUp handler for Tagcloud.
 * @param triggerOnAction
 * @param updatefocusedItemInfo
 * @param focusedItemInfo
 * @returns
 */
export function getKeyUpHandler(
  triggerOnAction: () => void,
  updatefocusedItemInfo: (itemInfo: ItemInfo) => void,
  focusedItemInfo: ItemInfo
) {
  return (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Space': {
        triggerOnAction();
        cancelEvent(event);
        break;
      }
      case 'Enter': {
        triggerOnAction();
        cancelEvent(event);
        break;
      }
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
        break;
      }
    }
  };
}
